import { AsyncPipe, CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { WizardComponent, WizardStepComponent } from 'mbs-ui-kit';
import { RestoreFileModelForStepHelpers } from 'wizards/helpers/restore-file-helpers/restore-file-model-for-step-helpers';
import { RestoreFilePlanFromStepsHelpers } from 'wizards/helpers/restore-file-helpers/restore-file-plan-from-steps-helpers';
import { RestoreType } from 'wizards/models/restore-point-models';
import { RestoreFileFormModel } from 'wizards/models/restoreFile/restore-file-form-model';
import { WizardHelpLink } from 'wizards/remote-management-wizards/strategy/wizard-help-link.class';
import { BackupToRestoreStepComponent } from 'wizards/steps/backup-to-restore-step/backup-to-restore-step.component';
import { DestinationStepComponent } from 'wizards/steps/destination-step/destination-step.component';
import { EncryptionOptionsStepComponent } from 'wizards/steps/encription-options-step/encryption-options-step.component';
import { NotificationsStepComponent } from 'wizards/steps/notifications-step/notifications-step.component';
import { PlanNameStepComponent } from 'wizards/steps/plan-name-step/plan-name-step.component';
import { PrePostActionsStepComponent } from 'wizards/steps/pre-post-actions-step/pre-post-actions-step.component';
import { RestorePointStepComponent } from 'wizards/steps/restore-point-step/restore-point-step.component';
import { RestoreSourceStepComponent } from 'wizards/steps/restore-source-step/restore-source-step.component';
import { ScheduleAdvancedStepComponent } from 'wizards/steps/schedule-advanced-step/schedule-advanced-step.component';
import { ScheduleStepComponent } from 'wizards/steps/schedule-step/schedule-step.component';
import { SelectComputerStepComponent } from 'wizards/steps/select-computer-step/select-computer-step.component';
import { WhereBackupStepComponent } from 'wizards/steps/where-backup-step/where-backup-step.component';
import { WizardBaseClass } from '../wizard-base.class';

type RestoreFileWizardHelpDocumentationLinks =
  | 'welcome'
  | 'whereToBackUp'
  | 'selectComputer'
  | 'selectBackup'
  | 'selectPoint'
  | 'selectSource'
  | 'destination'
  | 'encryption'
  | 'schedule'
  | 'prePost'
  | 'notification';

@UntilDestroy()
@Component({
  selector: 'mbs-restore-file-wizard',
  templateUrl: './restore-file-wizard.component.html',
  imports: [
    AsyncPipe,
    BackupToRestoreStepComponent,
    CommonModule,
    DestinationStepComponent,
    EncryptionOptionsStepComponent,
    FormsModule,
    NotificationsStepComponent,
    PrePostActionsStepComponent,
    RestorePointStepComponent,
    RestoreSourceStepComponent,
    ScheduleAdvancedStepComponent,
    ScheduleStepComponent,
    SelectComputerStepComponent,
    WhereBackupStepComponent,
    WizardComponent,
    WizardStepComponent,
    I18NextPipe,
    PlanNameStepComponent
  ]
})
export class RestoreFileWizardComponent extends WizardBaseClass {
  public restoreType = RestoreType;
  public restorePointTitle = this.i18nPipe.transform('wizards:restore_point_title');
  public helpLink: WizardHelpLink<RestoreFileWizardHelpDocumentationLinks>;

  constructor() {
    super();

    this.FormModel = new RestoreFileFormModel(this.isNBF, this.isUnix);

    this.initSubscribes();
  }

  updateStepData(plan, isDefault = false): void {
    this.loadedPlan = plan;
    if (this.isEdit && !this.isRDMode) this.mainService.GetPlanNotificationOptions();

    if (isDefault) return this.setDefaultValue();

    this.FormModel = RestoreFileModelForStepHelpers.RestoreFileModelFromPlan(plan, this.defaultSettings());

    this.stepsManager.showEncryptionOptionsStepIfNeed();
  }

  protected getPlanForSaving(): any {
    return RestoreFilePlanFromStepsHelpers.getUpdatedPlan(this.loadedPlan.Plan, this.FormModel, this.defaultSettings());
  }

  protected loadInfoHandlerFinally() {
    this.cdr.detectChanges();
  }

  getRAWHelpLinks(): Record<RestoreFileWizardHelpDocumentationLinks, string> {
    return {
      welcome: 'restore/file/file-restore/#planname',
      whereToBackUp: 'restore/file/file-restore/#storage',
      selectComputer: 'restore/file/file-restore/#comp',
      selectBackup: 'restore/file/file-restore/#backup',
      selectPoint: `restore/file/file-restore/#rp`,
      selectSource: `restore/file/file-restore/#rs`,
      destination: `restore/file/file-restore/#destination`,
      encryption: `restore/file/file-restore/#encryption`,
      schedule: `restore/file/file-restore/#schedule`,
      prePost: `restore/file/file-restore/#prepost`,
      notification: `restore/file/file-restore/#notification`
    };
  }
}
